import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  TextField,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { ThreeDots } from "react-loader-spinner";
import {
  Info,
  CheckCircle,
  Warning,
  ReportProblem,
  DoneAll,
} from "@material-ui/icons";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import CardBody from "components/Card/CardBody";
import FeedbackButton from "components/CTSGuide/FeedbackButton";
import { useTranslation } from "react-i18next";
import { client } from "services/axiosClient";
import ctsGuidePortalStyle from "assets/jss/material-dashboard-pro-react/views/ctsGuidePortalStyle";
import useThemeColors from "hooks/useThemeColors";
import CTSMiniNav from "components/CTSGuide/CTSMiniNav";

const useStyles = makeStyles(ctsGuidePortalStyle);

export default function BestPractices() {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { clientColor } = useThemeColors();
  const [practices, setPractices] = useState([]);
  const [progress, setProgress] = useState({
    confirmed_rules: [],
    impediments: {},
    checklist_progress_percentage: 0,
  });
  const [loading, setLoading] = useState(true);

  // Dialog state for impediment
  const [impedimentDialogOpen, setImpedimentDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState("");
  const [impedimentText, setImpedimentText] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await client.get("/api/ctsguide/best-practices-data/");

      setPractices(res.data.best_practices || []);
      const progressData = res.data.progress || {};
      setProgress({
        confirmed_rules: progressData.confirmed_rules || [],
        impediments: progressData.impediments || {},
        checklist_progress_percentage:
          progressData.checklist_progress_percentage || 0,
      });
    } catch (error) {
      // Error loading best practices
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAllRules = async () => {
    try {
      // Confirm all rules that don't have impediments
      const allRuleIds = practices.map((p) => p.id.toString());
      const rulesWithImpediments = Object.keys(progress.impediments);
      const rulesToConfirm = allRuleIds.filter(
        (id) => !rulesWithImpediments.includes(id),
      );

      await client.post("/api/ctsguide/user-progress/", {
        confirmed_rules: rulesToConfirm,
        impediments: progress.impediments,
      });

      setProgress((prev) => ({
        ...prev,
        confirmed_rules: rulesToConfirm,
        checklist_progress_percentage: Math.round(
          (rulesToConfirm.length / practices.length) * 100,
        ),
      }));
    } catch (error) {
      // Error confirming rules
    }
  };

  const handleOpenImpedimentDialog = () => {
    setSelectedRule("");
    setImpedimentText("");
    setImpedimentDialogOpen(true);
  };

  const handleCloseImpedimentDialog = () => {
    setImpedimentDialogOpen(false);
    setSelectedRule("");
    setImpedimentText("");
  };

  const handleSubmitImpediment = async () => {
    if (!selectedRule || !impedimentText.trim()) return;

    try {
      // Remove from confirmed if it was there, add to impediments
      const updatedConfirmed = progress.confirmed_rules.filter(
        (id) => id !== selectedRule,
      );
      const updatedImpediments = {
        ...progress.impediments,
        [selectedRule]: impedimentText,
      };

      await client.post("/api/ctsguide/user-progress/", {
        confirmed_rules: updatedConfirmed,
        impediments: updatedImpediments,
      });

      setProgress((prev) => ({
        ...prev,
        confirmed_rules: updatedConfirmed,
        impediments: updatedImpediments,
        checklist_progress_percentage: Math.round(
          (updatedConfirmed.length / practices.length) * 100,
        ),
      }));

      handleCloseImpedimentDialog();
    } catch (error) {
      // Error reporting impediment
    }
  };

  const handleCancelImpediment = async (ruleId) => {
    try {
      const updatedImpediments = { ...progress.impediments };
      delete updatedImpediments[ruleId];

      // Add back to confirmed rules
      const updatedConfirmed = [...progress.confirmed_rules, ruleId.toString()];

      await client.post("/api/ctsguide/user-progress/", {
        confirmed_rules: updatedConfirmed,
        impediments: updatedImpediments,
      });

      setProgress((prev) => ({
        ...prev,
        confirmed_rules: updatedConfirmed,
        impediments: updatedImpediments,
        checklist_progress_percentage: Math.round(
          (updatedConfirmed.length / practices.length) * 100,
        ),
      }));
    } catch (error) {
      // Error canceling impediment
    }
  };

  const isRuleConfirmed = (ruleId) =>
    progress.confirmed_rules.includes(ruleId.toString());

  const hasImpediment = (ruleId) => !!progress.impediments[ruleId];

  const allRulesConfirmed =
    practices.length > 0 &&
    practices.every((p) => isRuleConfirmed(p.id) || hasImpediment(p.id)) &&
    Object.keys(progress.impediments).length === 0;

  const hasAnyImpediment = Object.keys(progress.impediments).length > 0;

  return (
    <div className={classes.pageContainer}>
      <Box display="flex" justifyContent="center" mb={0.5}>
        <CTSMiniNav />
      </Box>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="client" icon>
              <CardIcon color="client">
                <PlaylistAddCheckIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {t("ctsguide.nav.best_practices")}
              </h4>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className={classes.loadingContainer}>
                  <ThreeDots
                    width="120px"
                    color={clientColor}
                    ariaLabel="loading"
                  />
                </div>
              ) : (
                <>
                  {/* Header with action buttons - minimal style */}
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    mb={2}
                    style={{ gap: "8px" }}
                  >
                    {allRulesConfirmed && (
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ marginRight: "auto" }}
                      >
                        <CheckCircle
                          style={{
                            color: "#4caf50",
                            marginRight: "6px",
                            fontSize: "18px",
                          }}
                        />
                        <span style={{ color: "#4caf50", fontSize: "13px" }}>
                          {t("ctsguide.best_practices.all_confirmed")}
                        </span>
                      </Box>
                    )}

                    {hasAnyImpediment && (
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ marginRight: "auto" }}
                      >
                        <Warning
                          style={{
                            color: "#ff9800",
                            marginRight: "6px",
                            fontSize: "18px",
                          }}
                        />
                        <span style={{ color: "#ff9800", fontSize: "13px" }}>
                          {Object.keys(progress.impediments).length}{" "}
                          {t("ctsguide.best_practices.impediments_count")}
                        </span>
                      </Box>
                    )}

                    {!allRulesConfirmed && (
                      <Tooltip title={t("ctsguide.best_practices.confirm_all")}>
                        <span>
                          <Button
                            size="sm"
                            justIcon
                            color="success"
                            onClick={handleConfirmAllRules}
                          >
                            <DoneAll />
                          </Button>
                        </span>
                      </Tooltip>
                    )}

                    <Tooltip
                      title={t("ctsguide.best_practices.report_impediment")}
                    >
                      <span>
                        <Button
                          size="sm"
                          justIcon
                          color="warning"
                          onClick={handleOpenImpedimentDialog}
                        >
                          <ReportProblem />
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  {/* Rules List */}
                  {practices.map((practice) => {
                    const confirmed = isRuleConfirmed(practice.id);
                    const impediment = hasImpediment(practice.id);
                    const impedimentDescription =
                      progress.impediments[practice.id];

                    return (
                      <div
                        key={practice.id}
                        className={`${classes.checklistItem} ${
                          confirmed ? classes.checklistItemConfirmed : ""
                        } ${impediment ? classes.checklistItemImpediment : ""}`}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                          }}
                        >
                          {/* Status icon - only show when confirmed or has impediment */}
                          {(confirmed || impediment) && (
                            <div style={{ marginTop: "2px" }}>
                              {confirmed && (
                                <CheckCircle
                                  style={{
                                    color: "#4caf50",
                                    fontSize: "16px",
                                  }}
                                />
                              )}
                              {impediment && (
                                <Warning
                                  style={{
                                    color: "#ff9800",
                                    fontSize: "16px",
                                  }}
                                />
                              )}
                            </div>
                          )}

                          <div style={{ flex: 1 }}>
                            <div className={classes.ruleText}>
                              <strong>
                                {practice.order}.{" "}
                                {practice.rule?.[i18n.language] || "Regra"}
                              </strong>
                              <Tooltip
                                title={practice.example?.[i18n.language] || ""}
                                placement="right"
                              >
                                <IconButton size="small">
                                  <Info fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>

                            <div className={classes.ruleExplanation}>
                              {practice.explanation?.[i18n.language] ||
                                "Explicação"}
                            </div>

                            {/* Show impediment description if exists */}
                            {impediment && impedimentDescription && (
                              <Box
                                mt={2}
                                p={2}
                                style={{
                                  backgroundColor: "#fff3e0",
                                  borderRadius: "8px",
                                  borderLeft: "4px solid #ff9800",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <div>
                                    <strong
                                      style={{
                                        color: "#e65100",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {t(
                                        "ctsguide.best_practices.impediment_reported",
                                      )}
                                      :
                                    </strong>
                                    <p
                                      style={{
                                        margin: "8px 0 0",
                                        color: "#5d4037",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {impedimentDescription}
                                    </p>
                                  </div>
                                  <Button
                                    size="sm"
                                    simple
                                    onClick={() =>
                                      handleCancelImpediment(practice.id)
                                    }
                                    style={{
                                      color: "#e65100",
                                      padding: "4px 8px",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {t(
                                      "ctsguide.best_practices.cancel_impediment",
                                    )}
                                  </Button>
                                </div>
                              </Box>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {practices.length === 0 && (
                    <Box textAlign="center" p={4}>
                      <p>{t("ctsguide.best_practices.no_practices")}</p>
                    </Box>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Impediment Dialog */}
      <Dialog
        open={impedimentDialogOpen}
        onClose={handleCloseImpedimentDialog}
        maxWidth="sm"
        fullWidth
      >
        <ModalHeader
          title={t("ctsguide.best_practices.report_impediment_title")}
          pills={[
            {
              key: "warning",
              label: t("common.warning"),
              tone: "warning",
              dot: true,
            },
          ]}
          onClose={handleCloseImpedimentDialog}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent>
          <Box mt={1}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="rule-select-label">
                {t("ctsguide.best_practices.select_rule")}
              </InputLabel>
              <Select
                labelId="rule-select-label"
                value={selectedRule}
                onChange={(e) => setSelectedRule(e.target.value)}
                label={t("ctsguide.best_practices.select_rule")}
              >
                {practices.map((practice) => (
                  <MenuItem key={practice.id} value={practice.id.toString()}>
                    {practice.order}.{" "}
                    {practice.rule?.[i18n.language] || "Regra"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mt={3}>
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                label={t("ctsguide.best_practices.impediment_description")}
                placeholder={t(
                  "ctsguide.best_practices.impediment_placeholder",
                )}
                value={impedimentText}
                onChange={(e) => setImpedimentText(e.target.value)}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button onClick={handleCloseImpedimentDialog} simple>
            {t("common.cancel")}
          </Button>
          <Tooltip title={t("common.submit")}>
            <span>
              <Button
                size="sm"
                justIcon
                color="success"
                onClick={handleSubmitImpediment}
                disabled={!selectedRule || !impedimentText.trim()}
              >
                <CheckCircle />
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Floating Feedback Button */}
      <FeedbackButton pageOrigin="bestPractices" />
    </div>
  );
}
